// DatePicker.tsx
import React, {useEffect, useRef} from 'react'
import flatpickr from 'flatpickr'
import 'flatpickr/dist/flatpickr.min.css'
import moment from 'moment'

interface DateRangePickerProps {
  label: string
  startName?: any
  endName?: any
  form?: any
  options?: any
  dateFormat?: string
  labelFormat?: string
  enableTime?: boolean
  dateOnly?: boolean
  isStatic?: boolean
  className?: string
  useFormik?: boolean
  onChange?: any
  startValue?: any
  endValue?: any
  closeOnSelect?: boolean
  disabled?: boolean
  minDate?: any
  maxDate?: any
  showToday?: boolean
  wrapperClass?: string[]
  isClearable?: boolean
}

const DateRangePicker: React.FC<DateRangePickerProps> = ({
  startName,
  endName,
  form,
  label = 'Pilih Tanggal',
  options,
  className = '',
  dateFormat = 'Y-m-d',
  labelFormat = 'd/m/Y',
  dateOnly,
  isStatic = true,
  useFormik = true,
  onChange,
  startValue,
  endValue,
  closeOnSelect,
  disabled = false,
  minDate,
  maxDate,
  showToday = false,
  wrapperClass,
  isClearable = false
}) => {
  const inputRef = useRef<HTMLInputElement>(null)
  const flatpickrInstance = useRef<flatpickr.Instance | null>(null)

  useEffect(() => {
    if (inputRef.current) {
      // console.log(inputRef.current)

      flatpickrInstance.current = flatpickr(inputRef.current, {
        ...options,
        dateFormat: dateFormat,
        altFormat: labelFormat,
        altInput: true,
        enableTime: false,
        time_24hr: true,
        defaultDate: startValue && endValue ? [startValue, endValue] : undefined,
        // static: true,
        closeOnSelect: false,
        mode: 'range',
        locale: {
          rangeSeparator: ' - ',
        },
        minDate: minDate,
        maxDate: maxDate,
        minTime: minDate,
        maxTime: maxDate,
        disableMobile: true,
        onClose: (selectedDates: Date[]) => {
          if (useFormik) {
            if (!selectedDates || selectedDates.length === 0) {
              onChange([])
              return
            }
            if (dateOnly) {
              form.setFieldValue(startName, moment(selectedDates[0]).format('YYYY-MM-DD'))

              if (selectedDates.length > 1) {
                form.setFieldValue(endName, moment(selectedDates[1]).format('YYYY-MM-DD'))
              } else {
                form.setFieldValue(endName, moment(selectedDates[0]).format('YYYY-MM-DD'))
              }
              return
            } else {
              form.setFieldValue(startName, moment(selectedDates[0]).startOf('days').toISOString())

              if (selectedDates.length > 1) {
                form.setFieldValue(endName, moment(selectedDates[1]).endOf('days').toISOString())
              } else {
                form.setFieldValue(endName, moment(selectedDates[0]).endOf('days').toISOString())
              }
              return
            }
          } else {
            if (!selectedDates || selectedDates.length === 0) {
              onChange([])
              return
            }
            if (dateOnly) {
              if (selectedDates.length > 1) {
                onChange([
                  moment(selectedDates[0]).format('YYYY-MM-DD'),
                  moment(selectedDates[1]).format('YYYY-MM-DD'),
                ])
              } else {
                onChange([
                  moment(selectedDates[0]).format('YYYY-MM-DD'),
                  moment(selectedDates[0]).format('YYYY-MM-DD'),
                ])
              }
            } else {
              if (selectedDates.length > 1) {
                onChange([
                  moment(selectedDates[0]).startOf('days').toISOString(),
                  moment(selectedDates[1]).endOf('days').toISOString(),
                ])
              } else {
                onChange([
                  moment(selectedDates[0]).startOf('days').toISOString(),
                  moment(selectedDates[0]).endOf('days').toISOString(),
                ])
              }
            }
          }
        },
        onChange: (selectedDates) => {
          if (selectedDates.length === 2) {
            flatpickrInstance.current?.close()
          }
        },
      })

      if (showToday) {
        const todayButton = document.createElement('button')
        todayButton.textContent = 'Hari Ini'
        todayButton.classList.add('flatpickr-today-btn')
        todayButton.classList.add('btn')
        todayButton.classList.add('btn-primary')
        todayButton.style.margin = '10px' // styling optional

        const calendarContainer = flatpickrInstance.current?.calendarContainer
        calendarContainer?.appendChild(todayButton)

        todayButton.addEventListener('click', function () {
          const today = new Date()
          flatpickrInstance.current?.setDate([today, today]) // Set tanggal ke hari ini
          flatpickrInstance.current?.close()
        })
      }

      if (wrapperClass) {
        wrapperClass.map((e) => {
          flatpickrInstance.current?.input?.closest('.flatpickr-wrapper')?.classList.add(e)
        })
      }
    }

    return () => {
      if (flatpickrInstance.current) {
        flatpickrInstance.current.destroy()
      }
    }
  }, [options, startValue, endValue, onChange])

  const handleClear = () => {
    flatpickrInstance.current?.setDate([])
    flatpickrInstance.current?.close()
  }

  return (
    <div className="flatpickr-wrapper" style={{position: 'relative', display: 'inline-block', width: '100%'}}>
      <input
        ref={inputRef}
        type='text'
        className={`form-control ${className}`}
        disabled={disabled}
        placeholder={label}
      />
      {flatpickrInstance.current?.selectedDates && isClearable && flatpickrInstance.current?.selectedDates.length === 2 && !disabled && (
        <button
          type="button"
          onClick={handleClear}
          style={{
            position: 'absolute',
            right: '10px',
            top: '50%',
            transform: 'translateY(-50%)',
            background: 'none',
            border: 'none',
            cursor: 'default',
            fontSize: '16px',
          }}
          className="react-select__indicator react-select__clear-indicator css-1xc3v61-indicatorContainer"
        >
          <svg height="20" width="20" viewBox="0 0 20 20" aria-hidden="true" focusable="false"
               className="css-tj5bde-Svg">
            <path
              d="M14.348 14.849c-0.469 0.469-1.229 0.469-1.697 0l-2.651-3.030-2.651 3.029c-0.469 0.469-1.229 0.469-1.697 0-0.469-0.469-0.469-1.229 0-1.697l2.758-3.15-2.759-3.152c-0.469-0.469-0.469-1.228 0-1.697s1.228-0.469 1.697 0l2.652 3.031 2.651-3.031c0.469-0.469 1.228-0.469 1.697 0s0.469 1.229 0 1.697l-2.758 3.152 2.758 3.15c0.469 0.469 0.469 1.229 0 1.698z"></path>
          </svg>
        </button>
      )}
    </div>
  )
}

export default DateRangePicker
